import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, ArrowLeft, AlertCircle, MapPin, Store, ShoppingBasket } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useCart } from "@/hooks/useCart";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import type { CartItem } from "@shared/schema";

interface BoxStopItem {
  itemId: string;
  itemName: string;
  unitPrice: number;
  qty: number;
  satisfiesIngredients: string[];
}

interface BoxStop {
  sellerId: string;
  sellerName: string;
  items: BoxStopItem[];
  subtotal: number;
  distanceFromBuyerMi: number | null;
}

interface UnmatchedIngredient {
  name: string;
  demandedByRecipeIds: string[];
  reason: "no_local_seller" | "dropped_for_radius";
}

interface BoxPlan {
  stops: BoxStop[];
  unmatched: UnmatchedIngredient[];
  totalSubtotal: number;
  maxPairwiseStopMiles: number | null;
  maxStopFromBuyerMi: number | null;
  withinRadiusConstraint: boolean;
  withinPickupWindowConstraint: boolean;
}

interface StoredSelection {
  zip: string;
  selections: { recipeId: string; servings: number }[];
}

export default function BoxPreview() {
  const [, setLocation] = useLocation();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const { toast } = useToast();
  const [stored, setStored] = useState<StoredSelection | null>(null);
  const [atHome, setAtHome] = useState<Set<string>>(new Set());
  const [plan, setPlan] = useState<BoxPlan | null>(null);

  useEffect(() => {
    const raw = sessionStorage.getItem("recipeBoxSelection");
    if (!raw) return;
    try {
      setStored(JSON.parse(raw) as StoredSelection);
    } catch {
      setStored(null);
    }
  }, []);

  const buildMutation = useMutation<BoxPlan, Error, { zip: string; selections: StoredSelection["selections"]; ingredientsAtHome: string[] }>({
    mutationFn: async (input) => {
      const res = await apiRequest("POST", "/api/box-from-recipes", input);
      return res.json();
    },
    onSuccess: (result) => setPlan(result),
  });

  useEffect(() => {
    if (!stored) return;
    buildMutation.mutate({
      zip: stored.zip,
      selections: stored.selections,
      ingredientsAtHome: Array.from(atHome),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stored, atHome]);

  const allItems = useMemo(() => {
    if (!plan) return [] as { itemId: string; qty: number }[];
    return plan.stops.flatMap((stop) => stop.items.map((i) => ({ itemId: i.itemId, qty: i.qty })));
  }, [plan]);

  const handleAddAllToCart = () => {
    if (allItems.length === 0) return;
    const userId = (user as { id?: string } | null | undefined)?.id;
    if (userId) {
      try {
        const key = `marketlink.cart.${userId}`;
        const existing = JSON.parse(localStorage.getItem(key) ?? "[]") as CartItem[];
        const merged = [...existing];
        for (const { itemId, qty } of allItems) {
          const idx = merged.findIndex((c) => c.itemId === itemId);
          if (idx >= 0) merged[idx] = { ...merged[idx], qty: merged[idx].qty + qty };
          else merged.push({ itemId, qty });
        }
        localStorage.setItem(key, JSON.stringify(merged));
      } catch {
        for (const { itemId, qty } of allItems) addToCart(itemId, qty);
      }
    } else {
      for (const { itemId, qty } of allItems) addToCart(itemId, qty);
    }
    toast({
      title: "Added to your cart",
      description: `${allItems.length} item${allItems.length !== 1 ? "s" : ""} from ${plan?.stops.length ?? 0} stop${(plan?.stops.length ?? 0) !== 1 ? "s" : ""}.`,
    });
    sessionStorage.removeItem("recipeBoxSelection");
    setLocation("/checkout");
  };

  const toggleAtHome = (name: string) => {
    setAtHome((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  if (!stored) {
    return (
      <>
        <Header />
        <div className="container mx-auto max-w-2xl px-4 py-10 text-center">
          <h1 className="text-xl font-semibold">No recipe selection</h1>
          <p className="mt-2 text-sm text-muted-foreground">Pick recipes first, then come back here.</p>
          <Link href="/recipes">
            <Button className="mt-4">Choose recipes</Button>
          </Link>
        </div>
      </>
    );
  }

  const isBuilding = buildMutation.isPending && !plan;

  return (
    <>
      <Header />
      <div className="container mx-auto max-w-3xl px-4 pt-4 pb-32 sm:pt-6">
        <div className="mb-4 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setLocation(`/recipes?zip=${encodeURIComponent(stored.zip)}`)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted"
            data-testid="button-back-to-recipes"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold sm:text-2xl">Your box</h1>
            <p className="text-xs text-muted-foreground sm:text-sm">
              Built from {stored.selections.length} recipe
              {stored.selections.length !== 1 ? "s" : ""} for ZIP {stored.zip}
            </p>
          </div>
        </div>

        {isBuilding ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center text-sm text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span>Finding the closest neighbors&hellip;</span>
          </div>
        ) : buildMutation.isError ? (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <div className="font-medium">Couldn&rsquo;t build the box</div>
                <p className="mt-1 text-xs">{buildMutation.error?.message ?? "Try again."}</p>
              </div>
            </div>
          </div>
        ) : plan ? (
          <>
            <Card className="mb-4">
              <CardContent className="grid grid-cols-3 gap-2 p-4 text-center">
                <div>
                  <div className="text-xl font-semibold sm:text-2xl">{plan.stops.length}</div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Stop{plan.stops.length !== 1 ? "s" : ""}
                  </div>
                </div>
                <div>
                  <div className="text-xl font-semibold sm:text-2xl">${plan.totalSubtotal.toFixed(2)}</div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Subtotal</div>
                </div>
                <div>
                  <div className="text-xl font-semibold sm:text-2xl">
                    {plan.maxPairwiseStopMiles != null
                      ? `${plan.maxPairwiseStopMiles.toFixed(1)} mi`
                      : "—"}
                  </div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Stops apart
                  </div>
                </div>
              </CardContent>
            </Card>

            {!plan.withinRadiusConstraint ? (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                These stops are spread out. We tried to keep them close but couldn&rsquo;t cover everything within the
                preferred radius. You can drop a recipe to tighten it up.
              </div>
            ) : null}

            {!plan.withinPickupWindowConstraint ? (
              <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900">
                These sellers don&rsquo;t share a 2-hour pickup window in the next 7 days, so a community driver
                can&rsquo;t pick up from all of them in one trip. Drop a recipe or pick another to find sellers
                with overlapping schedules.
              </div>
            ) : null}

            {plan.stops.length > 1 ? (
              <p className="mb-3 text-[11px] text-muted-foreground">
                Community-delivery fee covers your neighbor driver in full — no tip needed.
              </p>
            ) : null}

            <div className="space-y-3">
              {plan.stops.map((stop, index) => (
                <Card key={stop.sellerId}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                          {index + 1}
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5 text-sm font-semibold">
                            <Store className="h-3.5 w-3.5 text-primary" />
                            {stop.sellerName}
                          </div>
                          {stop.distanceFromBuyerMi != null ? (
                            <div className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
                              <MapPin className="h-3 w-3" />~{stop.distanceFromBuyerMi.toFixed(1)} mi from you
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="text-sm font-semibold">${stop.subtotal.toFixed(2)}</div>
                    </div>

                    <ul className="mt-3 space-y-1.5">
                      {stop.items.map((item) => (
                        <li key={item.itemId} className="flex items-start justify-between gap-3 text-xs sm:text-sm">
                          <div className="min-w-0">
                            <div className="font-medium">{item.itemName}</div>
                            <div className="text-[11px] text-muted-foreground">
                              For: {item.satisfiesIngredients.join(", ")}
                            </div>
                          </div>
                          <div className="shrink-0 text-right">
                            <div>×{item.qty}</div>
                            <div className="text-[11px] text-muted-foreground">${item.unitPrice.toFixed(2)} ea</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>

            {plan.unmatched.length > 0 ? (
              <Card className="mt-4 border-amber-200 bg-amber-50/40">
                <CardContent className="p-4">
                  <div className="text-sm font-semibold">Not available locally</div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Tap an item to mark it as something you already have at home, and we&rsquo;ll rebuild without it.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {plan.unmatched.map((u) => {
                      const have = atHome.has(u.name);
                      return (
                        <button
                          key={u.name}
                          type="button"
                          onClick={() => toggleAtHome(u.name)}
                          className={`rounded-full border px-3 py-1 text-xs transition ${
                            have
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
                          }`}
                          data-testid={`unmatched-${u.name}`}
                        >
                          {have ? "✓ " : ""}
                          {u.name}
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ) : null}

            {buildMutation.isPending && plan ? (
              <div className="mt-3 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Rebuilding&hellip;
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      {plan && plan.stops.length > 0 ? (
        <div
          className="fixed inset-x-0 z-30 border-t bg-background/95 px-4 py-3 backdrop-blur"
          style={{ bottom: 52 }}
        >
          <div className="container mx-auto flex max-w-3xl items-center justify-between gap-3">
            <div className="text-sm">
              <span className="font-semibold">${plan.totalSubtotal.toFixed(2)}</span>
              <span className="text-muted-foreground"> · {allItems.length} items</span>
            </div>
            <Button onClick={handleAddAllToCart} data-testid="button-add-all-to-cart">
              <ShoppingBasket className="mr-1.5 h-4 w-4" />
              Add to cart
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}
